import { createTheme } from "@mui/material/styles";

/**
 * MUI in the studio (experiential tools, the session transcript Dialog, the
 * calendar) ran with no ThemeProvider, so its components fell back to MUI's
 * defaults — a Roboto font, a blue primary and a fixed white Paper that ignored
 * the active theme.
 *
 * This maps MUI's palette onto the studio's CSS tokens (see styles/tokens.css) so
 * MUI surfaces follow whichever theme is active (all 9 themes, incl. dark) instead
 * of MUI defaults. Background, text and divider are `var(--...)` so the raw color
 * follows the active theme.
 *
 * `cssVariables: true` is REQUIRED, not cosmetic: MUI derives hover/selected/border
 * tints by calling `alpha(paletteColor, opacity)`, and `alpha()` throws on a `var()`
 * string ("Unsupported var(--color-ink) color"). MUI v7 eagerly evaluates every
 * component variant's style function — so a single `<Button>` (its `color="inherit"`
 * variant alphas `text.primary`) crashes the whole render. With cssVariables on, MUI
 * takes the `theme.vars` branch — `rgba(var(--...-primaryChannel) / opacity)` — and
 * never calls `alpha()`, so the crash cannot happen for ANY var-backed slot.
 *
 * The `*Channel` tokens below are the base (Reading Room) theme's colors as "R G B"
 * triplets. MUI can't derive a channel from an opaque `var()` (it would warn and
 * emit an invalid `rgba()`), so we provide them explicitly: alpha-blended tints
 * (inherit-button hover, table borders) render correctly in the base theme, while
 * every raw color still follows the active theme. `primary` stays a concrete hex —
 * MUI also computes light/dark variants from it. The palette lives under
 * `colorSchemes.light` because the `*Channel` tokens are only accepted there (they
 * are not part of createTheme's top-level `palette` option type).
 */
const muiTheme = createTheme({
  cssVariables: true,
  colorSchemes: {
    light: {
      palette: {
        primary: { main: "#46614f", contrastText: "#f6f1e5" },
        background: {
          default: "var(--color-bg)",
          defaultChannel: "243 237 222", // --color-bg (Reading Room)
          paper: "var(--color-surface)",
          paperChannel: "250 246 234", // --color-surface (Reading Room)
        },
        text: {
          primary: "var(--color-ink)",
          primaryChannel: "43 54 47", // --color-ink (Reading Room)
          secondary: "var(--color-muted)",
          secondaryChannel: "131 136 123", // --color-muted (Reading Room)
        },
        divider: "var(--color-border)",
        dividerChannel: "222 213 189", // --color-border (Reading Room)
      },
    },
  },
  shape: { borderRadius: 12 },
  typography: {
    fontFamily: "var(--font-sans)",
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        // MUI's elevation overlay lightens paper toward white — drop it so
        // dialogs/menus keep the warm surface tone at every elevation.
        root: { backgroundImage: "none" },
      },
    },
  },
});

export default muiTheme;
